import axios from 'axios'

// API Base URL - change this based on your environment
export const API_BASE_URL = 'https://dashboard.byredstone.com/api'

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache'
  }
})

// Request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    console.log('[API Request]', config.method?.toUpperCase(), config.baseURL + config.url)
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor for debugging
api.interceptors.response.use(
  (response) => {
    console.log('[API Response]', response.config.url, 'Status:', response.status)
    return response
  },
  (error) => {
    console.error('[API Error]', error.config?.url, error.message, error.response?.data)
    return Promise.reject(error)
  }
)

// ============================================
// AUTH API - POST /login returns user object directly
// ============================================
export const authApi = {
  login: (username, password) => api.post('/login', { username, password })
}

// ============================================
// GENERIC TABLE API - /tables/:table returns ARRAY directly
// ============================================
const createTableApi = (tableName) => ({
  getAll: (params = {}) => api.get(`/tables/${tableName}`, { params }),
  getByField: (field, val) => api.get(`/tables/${tableName}`, { params: { field, val } }),
  getById: (id) => api.get(`/tables/${tableName}`, { params: { field: 'id', val: id } }),
  create: (data) => api.post(`/tables/${tableName}/add`, data),
  update: (id, data) => api.put(`/tables/${tableName}/update/${id}`, data),
  delete: (id) => api.delete(`/tables/${tableName}/delete`, { params: { field: 'id', val: id } })
})

// ============================================
// AVATARS API
// ============================================
export const avatarApi = {
  ...createTableApi('avatars'),
  archive: (id) => api.put(`/tables/avatars/update/${id}`, { archived: 1 }),
  unarchive: (id) => api.put(`/tables/avatars/update/${id}`, { archived: 0 })
}

// ============================================
// DIALERS API
// ============================================
export const dialerApi = {
  ...createTableApi('dialers'),
  archive: (id) => api.put(`/tables/dialers/update/${id}`, { archived: 1 }),
  unarchive: (id) => api.put(`/tables/dialers/update/${id}`, { archived: 0 }),
  // Preview what cascade delete will affect (counts + avatar shared analysis)
  cascadePreview: (id) => api.get(`/tables/dialers/${id}/cascade-preview`),
  // Execute cascade delete with optional inclusion flags
  cascadeDelete: (id, body) => api.delete(`/tables/dialers/${id}/cascade`, { data: body })
}

// ============================================
// LICENSES API - has special getLicenses endpoint
// ============================================
export const licenseApi = {
  ...createTableApi('licenses'),
  getAllWithAvatars: () => api.get('/tables/getLicenses'),
  bulkUpdate: async (ids, data) => {
    const promises = ids.map(id => api.put(`/tables/licenses/update/${id}`, data))
    return Promise.all(promises)
  }
}

// ============================================
// USERS API
// ============================================
export const userApi = createTableApi('users')

// ============================================
// LINKS API - uses 'links' table (NOT access_links)
// Stores dialers and links as JSON strings in a single record
// ============================================
export const linksApi = createTableApi('links')

// ============================================
// VOICE SETS API - /voice-sets returns array directly
// ============================================
export const voiceSetApi = {
  // Basic CRUD
  getAll: () => api.get('/voice-sets'),
  getById: (id) => api.get(`/voice-sets/${id}`),
  create: (data) => api.post('/voice-sets', data),
  update: (id, data) => api.put(`/voice-sets/${id}`, data),
  delete: (id) => api.delete(`/voice-sets/${id}`),
  clone: (id, name) => api.post(`/voice-sets/${id}/clone`, { name }),

  // Categories (per voice set)
  getCategories: (id) => api.get(`/voice-sets/${id}/categories`),
  addCategory: (id, data) => api.post(`/voice-sets/${id}/categories`, data),
  updateCategory: (voiceSetId, categoryId, data) =>
    api.put(`/voice-sets/${voiceSetId}/categories/${categoryId}`, data),
  deleteCategory: (voiceSetId, categoryId) =>
    api.delete(`/voice-sets/${voiceSetId}/categories/${categoryId}`),

  // Files
  uploadFiles: (voiceSetId, categoryId, files) => {
    const formData = new FormData()
    files.forEach(file => formData.append('files', file))
    return api.post(`/voice-sets/${voiceSetId}/categories/${categoryId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  bulkUpload: (voiceSetId, files, filePaths) => {
    const formData = new FormData()
    files.forEach(file => formData.append('files', file))
    formData.append('filePaths', JSON.stringify(filePaths))
    return api.post(`/voice-sets/${voiceSetId}/bulk-upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  // ============================================
  // Metadata generation endpoints
  // ============================================
  generateMetadata: (id, force = false) =>
    api.post(`/voice-sets/${id}/generate-metadata`, { force }),
  getMetadataStatus: (id) =>
    api.get(`/voice-sets/${id}/metadata-status`),
  retryFailedMetadata: (id) =>
    api.post(`/voice-sets/${id}/retry-failed`),
  getFilesWithMetadata: (id) =>
    api.get(`/voice-sets/${id}/files-with-metadata`),
  getCategoriesWithMetadata: (id) =>
    api.get(`/voice-sets/${id}/categories-with-metadata`),

  // ============================================
  // NEW: Global Categories (ElevenLabs Integration)
  // ============================================

  // Get all global categories with variations
  getGlobalCategories: () =>
    api.get('/voice-sets/global-categories'),

  // Create global category (triggers generation for all voice sets)
  createGlobalCategory: (data) =>
    api.post('/voice-sets/global-categories', data),

  // Update global category
  updateGlobalCategory: (id, data) =>
    api.put(`/voice-sets/global-categories/${id}`, data),

  // Delete global category (removes from ALL voice sets)
  deleteGlobalCategory: (id) =>
    api.delete(`/voice-sets/global-categories/${id}`),

  // ============================================
  // NEW: Generation Progress & Control
  // ============================================

  // Poll current generation job progress
  getGenerationProgress: () =>
    api.get('/voice-sets/generation-progress'),

  // Cancel current generation job
  cancelGeneration: () =>
    api.post('/voice-sets/generation-cancel'),

  // Get recent generation job history
  getGenerationHistory: () =>
    api.get('/voice-sets/generation-history'),

  // ============================================
  // NEW: ElevenLabs Utilities
  // ============================================

  // List available ElevenLabs voices
  getElevenLabsVoices: () =>
    api.get('/voice-sets/elevenlabs/voices'),

  // Test an ElevenLabs voice (returns base64 audio)
  testElevenLabsVoice: (voiceId, text) =>
    api.post('/voice-sets/elevenlabs/test', { voice_id: voiceId, text }),
}

// ============================================
// CHANGE LOG API - /change-log
// ============================================
export const changeLogApi = {
  // Get all change log entries with filters
  getAll: (params = {}) => api.get('/change-log', { params }),

  // Get changes for a specific entity
  getEntityChanges: (type, id, limit = 20) =>
    api.get(`/change-log/entity/${type}/${id}`, { params: { limit } }),

  // Get recent changes for entity type (voice_set, voice_category, avatar, license, engine_action)
  getRecent: (type, limit = 10) =>
    api.get(`/change-log/recent/${type}`, { params: { limit } }),

  // Log a change manually
  log: (data) => api.post('/change-log', data),
}

// ============================================
// KEYWORD CATEGORIES API - /keyword-categories
// ============================================
export const keywordApi = {
  // Existing endpoints
  getAll: () => api.get('/keyword-categories'),
  getById: (id) => api.get(`/keyword-categories/${id}`),
  create: (data) => api.post('/keyword-categories', data),
  update: (id, data) => api.put(`/keyword-categories/${id}`, data),
  delete: (id) => api.delete(`/keyword-categories/${id}`),

  // Keywords within a category
  addKeyword: (categoryId, data) => api.post(`/keyword-categories/${categoryId}/keywords`, data),
  updateKeyword: (categoryId, keywordId, data) => api.put(`/keyword-categories/${categoryId}/keywords/${keywordId}`, data),
  deleteKeyword: (categoryId, keywordId) => api.delete(`/keyword-categories/${categoryId}/keywords/${keywordId}`),
  bulkAddKeywords: (categoryId, keywords) => api.post(`/keyword-categories/${categoryId}/keywords/bulk`, { keywords }),

  // Stats & Suggestions endpoints
  getStats: (days = 7) => api.get('/keyword-categories/stats', { params: { days } }),
  getSuggestions: (days = 7, minOccurrences = 10) =>
    api.get('/keyword-categories/suggestions', { params: { days, min_occurrences: minOccurrences } }),
  getTopKeywords: (days = 7, limit = 50) =>
    api.get('/keyword-categories/top-keywords', { params: { days, limit } }),
  getDuplicates: () => api.get('/keyword-categories/duplicates'),
  getBotData: () => api.get('/keyword-categories/bot-data'),

  // NEW: Occurrences endpoint
  getOccurrences: (keyword, days = 7, limit = 200) =>
    api.get('/keyword-categories/occurrences', { params: { keyword, days, limit } }),
}

// ============================================
// SOUNDS API
// ============================================
export const soundApi = {
  getByAvatar: (avatarId) => api.get(`/blob/getFiles/${avatarId}`),
  getSoundData: (filename) => api.post('/blob/getSoundFileData', { filename }),
  trimFile: (filename) => api.post('/blob/trimFile', { filename }),
  getFromTable: (avatarId) => api.get('/tables/sounds', { params: { field: 'avatar_id', val: avatarId } }),
  create: (data) => api.post('/tables/sounds/add', data),
  update: (id, data) => api.put(`/tables/sounds/update/${id}`, data),
  delete: (id) => api.delete('/tables/sounds/delete', { params: { field: 'id', val: id } })
}

// ============================================
// ENGINE API
// ============================================
export const engineApi = {
  getActions: (avatarId) => api.get('/tables/engine_actions', { params: { field: 'avatar_id', val: avatarId } }),
  createAction: (data) => api.post('/tables/engine_actions/add', data),
  updateAction: (id, data) => api.put(`/tables/engine_actions/update/${id}`, data),
  deleteAction: (id) => api.delete('/tables/engine_actions/delete', { params: { field: 'id', val: id } }),
  getTransferSequence: (avatarId) => api.get('/tables/transfer_sequence', { params: { field: 'avatar_id', val: avatarId } }),
  getHangupSequence: (avatarId) => api.get('/tables/hangup_sequence', { params: { field: 'avatar_id', val: avatarId } })
}

// ============================================
// LOGS API
// ============================================
export const logsApi = {
  getAll: (params = {}) => api.get('/logs', { params }),
  getCallLogs: () => api.get('/logs/callLogs'),
  getCallLogsPaginated: (page = 1) => api.get('/logs/callLogsPagination', { params: { page } }),
  add: (data) => api.post('/logs/add', data),
  addCallLog: (data) => api.post('/logs/post-log', data),
  delete: (field, val) => api.delete('/logs/delete', { params: { field, val } }),
  queryRecords: (filters) => api.post('/logs/queryRecords', filters)
}

// ============================================
// ACCESS API
// ============================================
export const accessApi = {
  verify: (key) => api.get('/access/verify', { params: { key } })
}

// ============================================
// STATS API - aggregates data from other endpoints
// ============================================
export const statsApi = {
  getDashboard: async () => {
    const [avatars, dialers, licenses, voiceSets] = await Promise.allSettled([
      api.get('/tables/avatars'),
      api.get('/tables/dialers'),
      api.get('/tables/licenses'),
      api.get('/voice-sets')
    ])

    return {
      avatars: avatars.status === 'fulfilled' ? (Array.isArray(avatars.value.data) ? avatars.value.data.length : 0) : 0,
      dialers: dialers.status === 'fulfilled' ? (Array.isArray(dialers.value.data) ? dialers.value.data.length : 0) : 0,
      licenses: licenses.status === 'fulfilled' ? (Array.isArray(licenses.value.data) ? licenses.value.data.length : 0) : 0,
      voiceSets: voiceSets.status === 'fulfilled' ? (Array.isArray(voiceSets.value.data) ? voiceSets.value.data.length : 0) : 0
    }
  }
}
// ============================================
// MONITORING API - CLEANED UP (NO DUPLICATES)
// ============================================
export const monitoringApi = {
  // Dashboard summary
  /**
   * Get call logs with comprehensive filtering
   * @param {Object} params - Filter parameters
   * @param {string} params.avatar_id - Filter by avatar
   * @param {string} params.dialer_id - Filter by dialer
   * @param {string} params.sip_id - Filter by SIP
   * @param {string} params.disposition - Filter by disposition (comma-separated for multiple)
   * @param {string} params.was_transferred - Filter transfers only ('true'/'false')
   * @param {string} params.from - Start date (YYYY-MM-DD)
   * @param {string} params.to - End date (YYYY-MM-DD)
   * @param {number} params.page - Page number
   * @param {number} params.limit - Results per page
   */
  getCallLogs: (params = {}) => api.get('/monitoring/call-logs', { params }),
  search: (params) => api.get('/monitoring/search', { params }),

  /**
   * Get full timeline/events for a specific call
   * Includes transcriptions (FG/BG), bot audio, interruptions, step changes
   * @param {string} callId - The call ID
   */
  getCallEvents: (callId) => api.get(`/monitoring/call-events/${callId}`),

  /**
   * Get SIPs filtered by avatar or dialer
   * @param {Object} params - { avatar_id, dialer_id }
   */
  getSips: (params = {}) => api.get('/monitoring/sips', { params }),

  /**
   * Get disposition options for filter dropdown
   * @param {Object} params - { from, to }
   */
  getDispositionOptions: (params = {}) => api.get('/monitoring/disposition-options', { params }),

  getSummary: () => api.get('/monitoring/summary'),

  // Bot status
  getAllStatus: (params) => api.get('/monitoring/status', { params }),
  getBotStatus: (sipId) => api.get(`/monitoring/status/${sipId}`),

  // Dispositions list
  getDispositions: (params) => api.get('/monitoring/dispositions', { params }),

  // ============================================
  // STATS ENDPOINTS (with filter support)
  // ============================================

  /**
   * Get comprehensive overview stats
   * @param {Object} params - { from, to, sip_id, dialer_id, voice_set_id, avatar_id }
   */
  getStatsOverview: (params = {}) => api.get('/monitoring/stats/overview', { params }),

  /**
   * Get disposition breakdown with percentages
   * @param {Object} params - { from, to, sip_id, dialer_id, voice_set_id, avatar_id }
   */
  getDispositionStats: (params = {}) => api.get('/monitoring/stats/dispositions', { params }),

  /**
   * Get stats grouped by avatar
   * @param {Object} params - { from, to, dialer_id }
   */
  getStatsByAvatar: (params = {}) => api.get('/monitoring/stats/by-avatar', { params }),

  /**
   * Get stats grouped by voice set
   * @param {Object} params - { from, to, dialer_id, avatar_id }
   */
  getStatsByVoiceSet: (params = {}) => api.get('/monitoring/stats/by-voice-set', { params }),

  /**
   * Get stats grouped by dialer
   * @param {Object} params - { from, to, avatar_id, voice_set_id }
   */
  getStatsByDialer: (params = {}) => api.get('/monitoring/stats/by-dialer', { params }),

  /**
   * Get stats grouped by bot (sip_id)
   * @param {Object} params - { from, to, dialer_id }
   */
  getStatsByBot: (params = {}) => api.get('/monitoring/stats/by-bot', { params }),

  /**
   * Get keyword stats
   */
  getKeywordStats: (params) => api.get('/monitoring/stats/keywords', { params }),

  /**
   * Get bots that have been inactive for N+ days
   * @param {number} days - Minimum days of inactivity (default 7)
   */
  getInactiveBots: (days = 7) => api.get('/monitoring/stats/inactive-bots', { params: { days } }),

  // ============================================
  // CALL DETAILS
  // ============================================

  /**
   * Get transcript for a specific call
   * @param {string} callId
   */
  getTranscript: (callId) => api.get(`/monitoring/transcripts/${callId}`),

  /**
   * Get keywords matched in a specific call
   * @param {string} callId
   */
  getCallKeywords: (callId) => api.get(`/monitoring/keywords/${callId}`),

  // ============================================
  // ERRORS
  // ============================================
  getErrors: (params) => api.get('/monitoring/errors', { params }),
  resolveError: (errorId, notes) => api.put(`/monitoring/errors/${errorId}/resolve`, { notes }),

  // ============================================
  // SETTINGS
  // ============================================
  getSettings: () => api.get('/monitoring/settings'),
  updateSettings: (sipId, settings) => api.put(`/monitoring/settings/${sipId}`, settings),
  updateGlobalSettings: (settings) => api.put('/monitoring/settings/global', settings),

  // ============================================
  // BOT CONTROL (v3.92)
  // ============================================

  /**
   * Get dialers summary with bot counts and issues
   * Returns: { totals, dialers, issues }
   */
  getDialersSummary: () => api.get('/monitoring/dialers-summary'),

  /**
   * Get all bots linked to a specific dialer
   * @param {number} dialerId
   * Returns: { bots: [...] } with calls_today, calls_7d, transfers, etc.
   */
  getBotsByDialer: (dialerId) => api.get(`/monitoring/bots-by-dialer/${dialerId}`),

  /**
   * Send command to one or more bots
   * @param {string[]} sipIds - Array of SIP IDs
   * @param {string} command - 'start' | 'stop' | 'restart_vm'
   * @param {number} dialerId - Optional dialer ID to target specific dialer
   */
  sendCommand: (sipIds, command, dialerId) => api.post('/monitoring/command', {
    sip_ids: sipIds,
    command,
    dialer_id: dialerId
  }),

  /**
   * Get command history
   * @param {Object} params - { sip_id, status, limit }
   */
  getCommands: (params = {}) => api.get('/monitoring/commands', { params }),

  // ============================================
  // VM CONTROL (Proxmox)
  // ============================================

  /**
   * Start a VM
   * @param {string} serverIP - Proxmox server IP
   * @param {number} vmid - VM ID
   * @param {string} sipId - Optional SIP ID for logging
   */
  vmStart: (serverIP, vmid, sipId, dialerId = null) => api.post('/monitoring/vm/start', {
    server_ip: serverIP,
    vmid,
    sip_id: sipId,
    dialer_id: dialerId
  }),

  /**
   * Stop a VM
   * @param {string} serverIP - Proxmox server IP
   * @param {number} vmid - VM ID
   * @param {string} sipId - Optional SIP ID for logging
   * @param {number} dialerId - Optional dialer ID to target specific bot
   */
  vmStop: (serverIP, vmid, sipId, dialerId = null) => api.post('/monitoring/vm/stop', {
    server_ip: serverIP,
    vmid,
    sip_id: sipId,
    dialer_id: dialerId
  }),

  /**
   * Restart a VM (stop + start)
   * @param {string} serverIP - Proxmox server IP
   * @param {number} vmid - VM ID
   * @param {string} sipId - Optional SIP ID for logging
   * @param {boolean} autoSignIn - Auto sign in to ViciDial after restart
   * @param {number} dialerId - Optional dialer ID to target specific bot_status
   */
  vmRestart: (serverIP, vmid, sipId, autoSignIn = false, dialerId = null) => api.post('/monitoring/vm/restart', {
    server_ip: serverIP,
    vmid,
    sip_id: sipId,
    auto_signin: autoSignIn,
    dialer_id: dialerId
  }),

  /**
   * Generic VM action (start/stop/restart)
   * Routes to the appropriate endpoint based on action
   * @param {string} serverIP - Proxmox server IP
   * @param {number} vmid - VM ID
   * @param {string} action - Action: 'start', 'stop', or 'restart'
   * @param {string} sipId - Optional SIP ID for logging
   * @param {number} dialerId - Optional dialer ID to target specific bot
   */
  vmAction: (serverIP, vmid, action, sipId = null, dialerId = null) => {
    const endpoint = `/monitoring/vm/${action}`
    return api.post(endpoint, {
      server_ip: serverIP,
      vmid,
      sip_id: sipId,
      dialer_id: dialerId
    })
  },

  /**
   * Get VM status
   * @param {string} serverIP - Proxmox server IP
   * @param {number} vmid - VM ID
   */
  vmStatus: (serverIP, vmid) => api.get('/monitoring/vm/status', {
    params: { server_ip: serverIP, vmid }
  }),

  /**
   * List all VMs on a server
   * @param {string} serverIP - Proxmox server IP
   */
  vmList: (serverIP) => api.get('/monitoring/vm/list', {
    params: { server_ip: serverIP }
  }),

  /**
   * Legacy restart VM method
   * @param {number} dialerId - Optional dialer ID to target specific bot_status
   */
  restartVM: (sipId, vmid, serverIP, dialerId = null) => api.post('/monitoring/restart-vm', {
    sip_id: sipId,
    vmid,
    server_ip: serverIP,
    dialer_id: dialerId
  }),

  /**
   * Get VM suggestion for a bot based on fuzzy matching
   * @param {string} serverIP - Proxmox server IP
   * @param {string} sipId - Bot SIP ID
   * @param {string} dialerName - Dialer name for matching pattern
   */
  vmSuggest: (serverIP, sipId, dialerName) => api.get('/monitoring/vm/suggest', {
    params: { server_ip: serverIP, sip_id: sipId, dialer_name: dialerName }
  }),

  /**
   * Link a bot to a VM
   * @param {string} sipId - Bot SIP ID
   * @param {number} vmid - Proxmox VM ID
   * @param {string} serverIP - Server IP where VM is hosted
   * @param {number} dialerId - Dialer ID to target specific bot (prevents cross-dialer issues)
   */
  linkBotToVM: (sipId, vmid, serverIP, dialerId) => api.post('/monitoring/bot/link-vm', {
    sip_id: sipId,
    vmid,
    server_ip: serverIP,
    dialer_id: dialerId
  }),

  /**
   * Unlink a bot from its VM
   * @param {string} sipId - Bot SIP ID
   * @param {number} dialerId - Optional dialer ID to target specific bot_status
   */
  unlinkBotFromVM: (sipId, dialerId = null) => api.post('/monitoring/bot/unlink-vm', {
    sip_id: sipId,
    dialer_id: dialerId
  }),

  /**
   * Set auto-login for a bot (v3.95)
   * @param {string} sipId - Bot SIP ID
   * @param {boolean} enabled - Enable or disable auto-login
   * @param {number} dialerId - Optional dialer ID to target specific license
   */
  setAutoLogin: (sipId, enabled, dialerId) => api.post(`/monitoring/set-auto-login/${sipId}`, {
    enabled,
    dialer_id: dialerId
  }),

  /**
   * Set auto-revive for a bot (v3.95)
   * @param {string} sipId - Bot SIP ID
   * @param {boolean} enabled - Enable or disable auto-revive
   * @param {number} dialerId - Optional dialer ID to target specific license
   */
  setAutoRevive: (sipId, enabled, dialerId) => api.post(`/monitoring/set-auto-revive/${sipId}`, {
    enabled,
    dialer_id: dialerId
  }),

  /**
   * Get crash reports for a bot (v3.95)
   * @param {string} sipId - Bot SIP ID
   * @param {string} date - Optional date filter (YYYY-MM-DD)
   */
  getCrashReports: (sipId, date) => api.get(`/monitoring/crash-reports/${sipId}`, {
    params: date ? { date } : {}
  }),

  // ============================================
  // SERVER STATS (Proxmox)
  // ============================================

  /**
   * Get all servers with stats
   */
  getServersStats: () => api.get('/monitoring/servers/stats'),

  /**
   * Get stats for a specific server (force refresh)
   * @param {string} serverIP - Server IP address
   */
  getServerStats: (serverIP) => api.get(`/monitoring/servers/${encodeURIComponent(serverIP)}/stats`),

  /**
   * Get all VMs on a server with bot linkage
   * @param {string} serverIP - Server IP address
   */
  getServerVMs: (serverIP) => api.get(`/monitoring/servers/${encodeURIComponent(serverIP)}/vms`),

  /**
   * Get servers that have VMs for a specific dialer
   * @param {number} dialerId - Dialer ID
   */
  getServersByDialer: (dialerId) => api.get(`/monitoring/servers/by-dialer/${dialerId}`),

  /**
   * Force refresh stats for all servers
   */
  refreshAllServers: () => api.post('/monitoring/servers/refresh-all'),

  /**
   * Set template VMID for a server
   * @param {number} serverId - Server ID
   * @param {number} templateVmid - Template VM ID
   */
  setServerTemplate: (serverId, templateVmid) => api.put(`/monitoring/servers/${serverId}/template`, {
    template_vmid: templateVmid
  }),

  // ============================================
  // SERVER SETUP (Add New Server Wizard)
  // ============================================

  /**
   * Get available VM templates (Redstone VM)
   */
  getVMTemplates: () => api.get('/monitoring/vm-templates'),

  /**
   * Test connection to a Proxmox server
   * @param {object} data - { ip_address, username?, password?, node? }
   */
  testServerConnection: (data) => api.post('/monitoring/servers/test-connection', data),

  /**
   * Add a new server manually
   * @param {object} data - { ip_address, name?, username?, password?, node? }
   */
  addServer: (data) => api.post('/monitoring/servers/add', data),

  /**
   * Start Redstone VM download and restore on a server
   * @param {number} serverId - Server ID
   * @param {number} templateId - Template ID (optional)
   */
  startServerSetup: (serverId, templateId) => api.post(`/monitoring/servers/${serverId}/setup`, {
    template_id: templateId
  }),

  /**
   * Get server setup job progress
   * @param {string} jobId - Setup job ID
   */
  getServerSetupProgress: (jobId) => api.get(`/monitoring/server-setup-progress/${jobId}`),

  /**
   * Get all active server setup jobs
   */
  getServerSetupJobs: () => api.get('/monitoring/server-setup-jobs'),

  // ============================================
  // VM CONFIGURATION & CLONING
  // ============================================

  /**
   * Get VM configuration
   * @param {string} serverIP - Server IP
   * @param {number} vmid - VM ID
   */
  getVMConfig: (serverIP, vmid) => api.get(`/monitoring/vm/${vmid}/config`, {
    params: { server_ip: serverIP }
  }),

  /**
   * Update VM configuration (cores/RAM)
   * @param {string} serverIP - Server IP
   * @param {number} vmid - VM ID
   * @param {number} cores - Number of cores
   * @param {number} memoryMb - Memory in MB
   */
  updateVMConfig: (serverIP, vmid, cores, memoryMb) => api.put('/monitoring/vm/config', {
    server_ip: serverIP,
    vmid,
    cores,
    memory_mb: memoryMb
  }),

  /**
   * Clone a VM
   * @param {string} serverIP - Server IP
   * @param {number} sourceVmid - Source VM ID
   * @param {string} templateName - Template name for VM naming
   * @param {object} options - Clone options {startAfterClone, configureDhcp, templateId}
   */
  cloneVM: (serverIP, sourceVmid, templateName, options = {}) => api.post('/monitoring/vm/clone', {
    server_ip: serverIP,
    source_vmid: sourceVmid,
    template_name: templateName,
    start_after_clone: options.startAfterClone,
    configure_dhcp: options.configureDhcp !== false,
    template_id: options.templateId
  }),

  /**
   * Clone VM asynchronously (background job with progress)
   * @param {string} serverIP - Server IP
   * @param {number} sourceVmid - Source VM ID
   * @param {string} templateName - Template name for VM naming
   * @param {object} options - Clone options {vmName, startAfterClone, configureDhcp, templateId, autoActivate}
   * @returns {object} - { success, jobId, vmName }
   */
  cloneVMAsync: (serverIP, sourceVmid, templateName, options = {}) => api.post('/monitoring/vm/clone-async', {
    server_ip: serverIP,
    source_vmid: sourceVmid,
    template_name: templateName,
    vm_name: options.vmName,
    start_after_clone: options.startAfterClone,
    configure_dhcp: options.configureDhcp !== false,
    template_id: options.templateId,
    auto_activate: options.autoActivate !== false,
    vm_type: options.vm_type || options.vmType || 'qemu'
  }),

  /**
   * Get clone job progress
   * @param {string} jobId - Clone job ID
   * @returns {object} - { found, status, progress, message, vmid, vmName, error }
   */
  getCloneProgress: (jobId) => api.get(`/monitoring/clone-progress/${jobId}`),

  /**
   * Get all active clone jobs
   * @returns {object} - { success, jobs: [...] }
   */
  getCloneJobs: () => api.get('/monitoring/clone-jobs'),

  /**
   * Rename a VM
   * @param {string} serverIP - Server IP
   * @param {number} vmid - VM ID
   * @param {string} newName - New name for the VM
   */
  renameVM: (serverIP, vmid, newName) => api.post('/monitoring/vm/rename', {
    server_ip: serverIP,
    vmid: vmid,
    new_name: newName
  }),

  /**
   * Bulk clone VMs with queue system
   * @param {string} serverIP - Server IP
   * @param {number} sourceVmid - Source VM ID
   * @param {number} templateId - Template ID for activation
   * @param {number} count - Number of clones to create
   * @param {object} options - { autoActivate, autoStart }
   */
  bulkClone: (serverIP, sourceVmid, templateId, count, options = {}) => api.post('/monitoring/vm/bulk-clone', {
    server_ip: serverIP,
    source_vmid: sourceVmid,
    template_id: templateId,
    count,
    auto_activate: options.autoActivate !== false,
    auto_start: options.startAfterClone !== false,
    vm_type: options.vm_type || options.vmType || 'qemu'
  }),

  /**
   * Get bulk clone queue status
   * @param {string} queueId - Queue ID
   */
  getBulkCloneProgress: (queueId) => api.get(`/monitoring/bulk-clone/${queueId}`),

  /**
   * Get all active bulk clone queues
   */
  getBulkCloneQueues: () => api.get('/monitoring/bulk-clone-queues'),

  /**
   * Cancel a running bulk clone queue
   */
  cancelBulkClone: (queueId) => api.post(`/monitoring/bulk-clone/${queueId}/cancel`),

  /**
   * Bulk VM actions (start, stop, restart, delete)
   * @param {string} serverIP - Server IP
   * @param {number[]} vmids - Array of VM IDs
   * @param {string} action - 'start' | 'stop' | 'restart' | 'delete'
   * @param {boolean} deleteLicense - For delete action: also delete associated license, bot_status, and machine entry
   */
  bulkVMAction: (serverIP, vmids, action, deleteLicense = false) => api.post('/monitoring/vm/bulk-action', {
    server_ip: serverIP,
    vmids,
    action,
    delete_license: deleteLicense
  }),

  /**
   * Delete a VM
   * @param {string} serverIP - Server IP
   * @param {number} vmid - VM ID
   * @param {boolean} deleteLicense - Also delete associated license, bot_status, and machine entry
   */
  deleteVM: (serverIP, vmid, deleteLicense = false) => api.delete(`/monitoring/vm/${vmid}`, {
    params: { server_ip: serverIP, delete_license: deleteLicense }
  }),

  /**
   * Delete an orphaned bot with all related records (v3.98)
   * @param {string} sipId - SIP ID
   * @param {number} dialerId - Dialer ID
   * @param {boolean} deleteCallRecords - Also delete call disposition records
   */
  deleteBot: (sipId, dialerId, deleteCallRecords = false, deleteVMs = false) => api.delete(`/monitoring/bot/${sipId}`, {
    params: { dialer_id: dialerId, delete_call_records: deleteCallRecords, delete_vms: deleteVMs }
  }),

  /**
   * Bulk delete multiple bots, optionally including Proxmox VMs
   * @param {string[]} sipIds - Array of SIP IDs
   * @param {number} dialerId - Dialer ID
   * @param {boolean} deleteCallRecords - Also delete call disposition records
   * @param {boolean} deleteVMs - Also delete Proxmox VMs
   */
  bulkDeleteBots: (sipIds, dialerId, deleteCallRecords = false, deleteVMs = false) => api.post('/monitoring/bots/bulk-delete', {
    sip_ids: sipIds,
    dialer_id: dialerId,
    delete_call_records: deleteCallRecords,
    delete_vms: deleteVMs
  }),

  // ============================================
  // LICENSE VOICE SET POOL
  // ============================================

  /**
   * Get voice set pool for a license
   * @param {number} licenseId - License ID
   */
  getLicenseVoicePool: (licenseId) => api.get(`/monitoring/license/${licenseId}/voice-pool`),

  /**
   * Update voice set pool for a license
   * @param {number} licenseId - License ID
   * @param {number[]} voiceSetPool - Array of voice set IDs
   */
  updateLicenseVoicePool: (licenseId, voiceSetPool) => api.put(`/monitoring/license/${licenseId}/voice-pool`, {
    voice_set_pool: voiceSetPool
  }),

  /**
   * Update transcriber type for a license
   * @param {number} licenseId - License ID
   * @param {string} transcriberType - Transcriber type (VOSK, VOSK_V15L, WHISPER_S1, etc.)
   */
  updateLicenseTranscriber: (licenseId, transcriberType) => api.put(`/monitoring/license/${licenseId}/transcriber`, {
    transcriber_type: transcriberType
  })
}

// ============================================
// TEMPLATES API - /templates
// ============================================
export const templateApi = {
  getAll: () => api.get('/templates'),
  getById: (id) => api.get(`/templates/${id}`),
  create: (data) => api.post('/templates', data),
  update: (id, data) => api.put(`/templates/${id}`, data),
  delete: (id) => api.delete(`/templates/${id}`),
  getAvailableSips: (id) => api.get(`/templates/${id}/available-sips`),
  parseSipPool: (sip_pool) => api.post('/templates/parse-sip-pool', { sip_pool })
}

// ============================================
// MACHINES API - /machines
// ============================================
export const machineApi = {
  getAll: (params = {}) => api.get('/machines', { params }),
  getPending: () => api.get('/machines/pending'),
  getStats: () => api.get('/machines/stats'),
  check: (data) => api.post('/machines/check', data),
  heartbeat: (data) => api.post('/machines/heartbeat', data),
  bulkActivate: (data) => api.post('/machines/bulk-activate', data),
  bulkReactivate: (data) => api.post('/machines/bulk-reactivate', data),

  /**
   * Deactivate a machine
   * @param {number} id - Machine ID
   * @param {boolean} keepCredentials - Keep SIP credentials in pool (default: true)
   */
  deactivate: (id, keepCredentials = true) => api.post(`/machines/${id}/deactivate`, {
    keep_credentials: keepCredentials
  }),
  reactivate: (id) => api.post(`/machines/${id}/reactivate`),
  updateLicense: (id, data) => api.put(`/machines/${id}/license`, data),
  delete: (id) => api.delete(`/machines/${id}`),

  /**
   * Set bot mode (production/development) for git branch switching
   * @param {number} licenseId - License ID
   * @param {string} botMode - 'production' or 'development'
   */
  setBotMode: (licenseId, botMode) => api.put(`/machines/license/${licenseId}/bot-mode`, { bot_mode: botMode }),

  /**
   * Get available licenses in pool with SIP credentials
   * For reassigning to new machines
   */
  getAvailableLicenses: () => api.get('/machines/available-licenses'),

  // Server Authorization
  getServers: () => api.get('/machines/servers'),
  getPendingServers: () => api.get('/machines/servers/pending'),
  approveServer: (id, data = {}) => api.post(`/machines/servers/${id}/approve`, data),
  blockServer: (id) => api.post(`/machines/servers/${id}/block`),
  updateServer: (id, data) => api.put(`/machines/servers/${id}`, data),
  deleteServer: (id) => api.delete(`/machines/servers/${id}`)
}

// ============================================
// BACKUP API - /backup
// ============================================
export const backupApi = {
  getStats: () => api.get('/backup/stats'),
  trigger: (name = null) => api.post('/backup/trigger', { name })
}

// ============================================
// CLIENT ADMIN API - /client-admin
// ============================================
export const clientAdminApi = {
  // Clients
  getClients: () => api.get('/client-admin/clients'),
  getClient: (id) => api.get(`/client-admin/clients/${id}`),
  createClient: (data) => api.post('/client-admin/clients', data),
  updateClient: (id, data) => api.put(`/client-admin/clients/${id}`, data),
  deleteClient: (id, hardDelete = false) => api.delete(`/client-admin/clients/${id}?hard_delete=${hardDelete}`),

  // Dialer Assignment
  getClientDialers: (id) => api.get(`/client-admin/clients/${id}/dialers`),
  assignDialer: (clientId, dialerId, customPrice, campaignType) => api.post(`/client-admin/clients/${clientId}/dialers`, { dialer_id: dialerId, custom_bot_price: customPrice, campaign_type: campaignType }),
  updateDialerAssignment: (clientId, dialerId, data) => api.put(`/client-admin/clients/${clientId}/dialers/${dialerId}`, data),
  removeDialer: (clientId, dialerId) => api.delete(`/client-admin/clients/${clientId}/dialers/${dialerId}`),
  getAvailableDialers: () => api.get('/client-admin/available-dialers'),

  // Billing (with user tracking)
  addCredit: (clientId, amount, description, userId, userName) => api.post(`/client-admin/clients/${clientId}/credit`, { amount, description, _user_id: userId, _user_name: userName }),
  setBalance: (clientId, balance, reason, userId, userName) => api.put(`/client-admin/clients/${clientId}/balance`, { balance, reason, _user_id: userId, _user_name: userName }),
  extendClient: (clientId, days, reason) => api.post(`/client-admin/clients/${clientId}/extend`, { days, reason }),
  setExtension: (clientId, days, reason) => api.put(`/client-admin/clients/${clientId}/extension`, { days, reason }),
  getBillingHistory: (clientId, limit = 50) => api.get(`/client-admin/clients/${clientId}/billing-history?limit=${limit}`),

  // Requests
  getRequests: (status = 'pending') => api.get(`/client-admin/requests?status=${status}`),
  updateRequest: (id, data) => api.put(`/client-admin/requests/${id}`, data),

  // Bot Changes (Dialer-Based Bot Tracking)
  getBotChanges: (clientId, dialerId) => api.get(`/client-admin/clients/${clientId}/bot-changes`, { params: dialerId ? { dialer_id: dialerId } : {} }),
  getBotChangesStats: (clientId) => api.get(`/client-admin/clients/${clientId}/bot-changes/stats`),
  addBotChange: (clientId, data) => api.post(`/client-admin/clients/${clientId}/bot-changes`, data),
  updateBotChange: (clientId, changeId, data) => api.put(`/client-admin/clients/${clientId}/bot-changes/${changeId}`, data),
  deleteBotChange: (clientId, changeId) => api.delete(`/client-admin/clients/${clientId}/bot-changes/${changeId}`),
  getBotTimeline: (clientId, periodStart, periodEnd) => api.get(`/client-admin/clients/${clientId}/bot-timeline`, { params: { period_start: periodStart, period_end: periodEnd } }),
  getBotChangesLog: (from, to) => api.get('/client-admin/bot-changes-log', { params: { from, to } }),
  addBotChangeLog: (data) => api.post('/client-admin/bot-changes-log', data),
  updateBotChangeLog: (id, data) => api.put(`/client-admin/bot-changes-log/${id}`, data),
  deleteBotChangeLog: (id) => api.delete(`/client-admin/bot-changes-log/${id}`),

  // Client Avatar Management (Admin)
  getClientAvatars: (clientId) => api.get(`/client-admin/clients/${clientId}/avatars`),
  assignClientAvatar: (clientId, data) => api.post(`/client-admin/clients/${clientId}/avatars`, data),
  removeClientAvatar: (clientId, avatarId) => api.delete(`/client-admin/clients/${clientId}/avatars/${avatarId}`),

  // Exchange Rate
  getExchangeRate: (force = false) => api.get('/client-admin/exchange-rate', { params: { force } }),

  // Invoices (with approval workflow and user tracking)
  getInvoices: (params = {}) => api.get('/client-admin/invoices', { params }),
  updateInvoiceStatus: (id, data) => api.put(`/client-admin/invoices/${id}/status`, data),
  updateInvoice: (id, data) => api.put(`/client-admin/invoices/${id}`, data),
  deleteInvoice: (id) => api.delete(`/client-admin/invoices/${id}`),
  generateInvoice: (data) => api.post('/client-admin/invoices/generate', data),
  approveInvoice: (id, userId, userName) => api.put(`/client-admin/invoices/${id}/approve`, { _user_id: userId, _user_name: userName }),
  unapproveInvoice: (id) => api.put(`/client-admin/invoices/${id}/unapprove`),
  getInvoicePdf: (id) => api.get(`/client-admin/invoices/${id}/pdf`, { responseType: 'blob' }),
  addLineItem: (invoiceId, data) => api.post(`/client-admin/invoices/${invoiceId}/line-items`, data),
  updateLineItem: (invoiceId, index, data) => api.put(`/client-admin/invoices/${invoiceId}/line-items/${index}`, data),
  deleteLineItem: (invoiceId, index) => api.delete(`/client-admin/invoices/${invoiceId}/line-items/${index}`),
  uploadInvoicePdf: (invoiceId, file) => {
    const formData = new FormData()
    formData.append('pdf', file)
    return api.post(`/client-admin/invoices/${invoiceId}/upload-pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  // Financial Dashboard
  getFinancialStats: (month) => api.get('/client-admin/financial-stats', { params: { month } }),
  getNotifications: () => api.get('/client-admin/notifications'),

  // Client Notes / Activity Log
  getClientNotes: (clientId, limit = 50) => api.get(`/client-admin/clients/${clientId}/notes`, { params: { limit } }),
  addClientNote: (clientId, data) => api.post(`/client-admin/clients/${clientId}/notes`, data),
  deleteClientNote: (clientId, noteId) => api.delete(`/client-admin/clients/${clientId}/notes/${noteId}`),

  // Bad Debt
  markBadDebt: (invoiceId, isBadDebt, reason) => api.put(`/client-admin/invoices/${invoiceId}/bad-debt`, { is_bad_debt: isBadDebt, reason }),

  // Invoice Payments
  getInvoicePayments: (invoiceId) => api.get(`/client-admin/invoices/${invoiceId}/payments`),
  getClientPayments: (clientId, limit = 100) => api.get(`/client-admin/clients/${clientId}/payments`, { params: { limit } }),
  recordPayment: (invoiceId, data) => api.post(`/client-admin/invoices/${invoiceId}/payments`, data),
  updatePayment: (paymentId, data) => api.put(`/client-admin/payments/${paymentId}`, data),
  deletePayment: (paymentId) => api.delete(`/client-admin/payments/${paymentId}`)
}

// ============================================
// FINANCIALS API - P&L, Server Costs, Reports
// ============================================
export const financialsApi = {
  // Dashboard & Reports
  getDashboard: (month, year) => api.get('/financials/dashboard', { params: { month, year } }),
  getRevenueByClient: (month, year) => api.get('/financials/revenue-by-client', { params: { month, year } }),

  // Server Costs
  getServers: () => api.get('/financials/servers'),
  addServer: (data) => api.post('/financials/servers', data),
  updateServer: (id, data) => api.put(`/financials/servers/${id}`, data),
  cancelServer: (id) => api.delete(`/financials/servers/${id}`),

  // Dialer Billing Control
  pauseDialer: (dialerId, reason) => api.post(`/financials/dialers/${dialerId}/pause`, { reason }),
  resumeDialer: (dialerId, reason) => api.post(`/financials/dialers/${dialerId}/resume`, { reason }),

  // Client Requests
  getRequests: (status = 'pending') => api.get('/financials/requests', { params: { status } }),
  updateRequest: (id, data) => api.put(`/financials/requests/${id}`, data),
  getRequestStats: () => api.get('/financials/requests/stats')
}

// ============================================
// SERVER HEALTH API - /server-health
// ============================================
export const serverHealthApi = {
  getStats: () => api.get('/server-health/stats'),
  getAlerts: () => api.get('/server-health/alerts')
}

// ============================================
// ISSUES API - Issue Tracker (v3.99)
// ============================================
export const issuesApi = {
  // Issues CRUD
  getAll: (params = {}) => api.get('/issues', { params }),
  getById: (id) => api.get(`/issues/${id}`),
  create: (data) => api.post('/issues', data),
  update: (id, data) => api.put(`/issues/${id}`, data),
  delete: (id) => api.delete(`/issues/${id}`),
  updateStatus: (id, status, userId) => api.put(`/issues/${id}/status`, { status, user_id: userId }),

  // Labels
  getLabels: () => api.get('/issues/labels'),
  createLabel: (data) => api.post('/issues/labels', data),
  updateLabel: (id, data) => api.put(`/issues/labels/${id}`, data),
  deleteLabel: (id) => api.delete(`/issues/labels/${id}`),
  addLabel: (issueId, labelId, userId) => api.post(`/issues/${issueId}/labels`, { label_id: labelId, user_id: userId }),
  removeLabel: (issueId, labelId, userId) => api.delete(`/issues/${issueId}/labels/${labelId}?user_id=${userId}`),

  // Comments
  getComments: (issueId) => api.get(`/issues/${issueId}/comments`),
  addComment: (issueId, content, userId, isResolution = false) =>
    api.post(`/issues/${issueId}/comments`, { content, user_id: userId, is_resolution: isResolution }),
  updateComment: (issueId, commentId, content) => api.put(`/issues/${issueId}/comments/${commentId}`, { content }),
  deleteComment: (issueId, commentId) => api.delete(`/issues/${issueId}/comments/${commentId}`),

  // Attachments
  uploadAttachment: (issueId, file, userId) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('user_id', userId)
    return api.post(`/issues/${issueId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  deleteAttachment: (issueId, attachmentId) => api.delete(`/issues/${issueId}/attachments/${attachmentId}`),
  getAttachmentUrl: (issueId, attachmentId) => `${API_BASE_URL}/issues/${issueId}/attachments/${attachmentId}/download`,

  // Activity
  getActivity: (issueId) => api.get(`/issues/${issueId}/activity`),

  // Stats
  getStats: () => api.get('/issues/stats')
}

export default api